import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const RESP_OPTS = [["accept", "Accetto"], ["decline", "Rifiuto"], ["unavailable", "Non sono disponibile"], ["propose", "Propongo un'altra data"]];

export function ConvocationDialog({ conv, contacts, onClose, onDone }) {
  const isNew = !conv.id;
  const [f, setF] = useState({ title: "", description: "", agenda: "", date: "", start_time: "", duration_min: 60, location: "", online_url: "", rsvp_by: "", ...conv, invitee_ids: conv.invitee_ids || [] });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (id) => setF({ ...f, invitee_ids: f.invitee_ids.includes(id) ? f.invitee_ids.filter((x) => x !== id) : [...f.invitee_ids, id] });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.invitee_ids.length) return toast.error("Seleziona almeno un amministratore");
    const body = { title: f.title, description: f.description, agenda: f.agenda, date: f.date, start_time: f.start_time, duration_min: Number(f.duration_min),
      location: f.location, online_url: f.online_url, invitee_ids: f.invitee_ids, rsvp_by: f.rsvp_by || null };
    try {
      await (isNew ? api.post("/network/convocations", body) : api.put(`/network/convocations/${conv.id}`, body));
      toast.success(isNew ? "Convocazione inviata" : "Convocazione aggiornata");
      onClose();
      onDone();
    } catch (e2) { toast.error(errMsg(e2)); }
  };
  const invitable = [...contacts, ...(conv.invitees || []).filter((i) => !contacts.some((c) => c.id === i.id))];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid="convocation-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Nuova convocazione" : "Modifica convocazione"}</DialogTitle><DialogDescription>Riunione, confronto operativo o assemblea tra soci.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>Titolo</Label><Input value={f.title} onChange={set("title")} required minLength={3} data-testid="convocation-title-input" /></div>
          <div className="space-y-1.5"><Label>Descrizione</Label><Textarea rows={2} value={f.description} onChange={set("description")} data-testid="convocation-description-input" /></div>
          <div className="space-y-1.5"><Label>Ordine del giorno</Label><Textarea rows={3} value={f.agenda} onChange={set("agenda")} data-testid="convocation-agenda-input" /></div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5"><Label>Data</Label><Input type="date" value={f.date} onChange={set("date")} required data-testid="convocation-date-input" /></div>
            <div className="space-y-1.5"><Label>Ora di inizio</Label><Input type="time" value={f.start_time} onChange={set("start_time")} required data-testid="convocation-time-input" /></div>
            <div className="space-y-1.5"><Label>Durata (min)</Label><Input type="number" min={5} max={1440} value={f.duration_min} onChange={set("duration_min")} required data-testid="convocation-duration-input" /></div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Luogo</Label><Input value={f.location} onChange={set("location")} placeholder="Indirizzo o sala" data-testid="convocation-location-input" /></div>
            <div className="space-y-1.5"><Label>Collegamento online</Label><Input type="url" value={f.online_url} onChange={set("online_url")} placeholder="https://" data-testid="convocation-url-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>Conferma entro (opzionale)</Label><Input type="date" value={f.rsvp_by || ""} onChange={set("rsvp_by")} data-testid="convocation-rsvp-input" /></div>
          <div className="space-y-2">
            <Label>Amministratori invitati</Label>
            <div className="grid max-h-48 gap-1.5 overflow-y-auto rounded-md border p-3 sm:grid-cols-2">
              {invitable.map((c) => (
                <label key={c.id} className="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox checked={f.invitee_ids.includes(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`convocation-invitee-${c.id}`} />
                  <span className="truncate">{c.name}<span className="ml-1 text-xs text-muted-foreground">{c.company}</span></span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter><Button type="submit" data-testid="convocation-submit-button">{isNew ? "Invia convocazione" : "Salva modifiche"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function RespondConvDialog({ conv, me, onClose, onDone }) {
  const prev = conv.responses[me] || {};
  const [f, setF] = useState({ status: prev.status || "accept", proposed_date: prev.proposed_date || "", proposed_time: prev.proposed_time || "", message: prev.message || "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/network/convocations/${conv.id}/respond`, { ...f, proposed_date: f.status === "propose" ? f.proposed_date : null, proposed_time: f.status === "propose" ? f.proposed_time || null : null });
      toast.success("Risposta inviata all'organizzatore");
      onClose();
      onDone();
    } catch (e2) { toast.error(errMsg(e2)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="convocation-respond-dialog">
        <DialogHeader><DialogTitle>{conv.title}</DialogTitle><DialogDescription>Rispondi alla convocazione di {conv.organizer?.name}.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <RadioGroup value={f.status} onValueChange={(v) => setF({ ...f, status: v })} className="grid gap-2">
            {RESP_OPTS.map(([v, l]) => (
              <label key={v} className="flex cursor-pointer items-center gap-2.5 rounded-md border p-2.5 text-sm"><RadioGroupItem value={v} data-testid={`convocation-response-${v}`} />{l}</label>
            ))}
          </RadioGroup>
          {f.status === "propose" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Data proposta</Label><Input type="date" value={f.proposed_date} onChange={(e) => setF({ ...f, proposed_date: e.target.value })} required data-testid="convocation-proposed-date-input" /></div>
              <div className="space-y-1.5"><Label>Ora</Label><Input type="time" value={f.proposed_time} onChange={(e) => setF({ ...f, proposed_time: e.target.value })} data-testid="convocation-proposed-time-input" /></div>
            </div>
          )}
          <div className="space-y-1.5"><Label>Messaggio all'organizzatore</Label><Textarea rows={3} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="convocation-response-message-input" /></div>
          <DialogFooter><Button type="submit" data-testid="convocation-response-submit">Invia risposta</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
